import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@material-ui/core';
import axios from 'axios';
import { toast } from 'react-toastify';
import { generateForm } from '../../helpers/functions';
import SingleCustomAutocomplete from '../CustomAutocomplete/SingleCustomAutocomplete';
import CustomMuiDataTable from '../CustomMuiDataTable';
import Swal from 'sweetalert2';

const PaymentsTab = ({ jobId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [clientPayments, setClientPayments] = useState([]);
  const [workerPayments, setWorkerPayments] = useState([]);
  const [addPaymentVisible, setAddPaymentVisible] = useState(false);
  const [paymentType, setPaymentType] = useState(null);
  const [formData, setFormData] = useState({
    job_id: jobId,
    amount: null,
    due_date: "",
    paid: false,
    type: null,
    method: "",
    id: -1,
  });

  useEffect(() => {
    if (jobId > 0) {
      loadAllPayments();
    }
  }, [jobId]);

  const loadAllPayments = () => {
    setLoading(true);
    Promise.all([
      loadPayments("in"),
      loadPayments("out")
    ]).finally(() => setLoading(false));
  };

  const loadPayments = (type) => {
    return axios
      .get(`${process.env.REACT_APP_API}/admin/jobs/getPayments`, {
        params: {
          id: jobId,
          type: type,
        },
      })
      .then((response) => {
        if (type === "in") {
          setClientPayments(response.data);
        } else {
          setWorkerPayments(response.data);
        }
      })
      .catch((error) => {
        toast.error(`Error loading ${type === 'in' ? 'client' : 'worker'} payments`);
      });
  };

  const handleInputChange = (e) => {
    const { id, value, checked, type } = e.target;
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [id]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [id]: value,
      });
    }
  };

  const customOnChange = (newValue, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSavePayment = () => {
    setLoading(true);
    const submitFormData = generateForm(formData);

    if (formData.id > 0) {
      axios({
        method: "post",
        url: `${process.env.REACT_APP_API}/admin/jobs/updatePayment`,
        data: submitFormData,
      })
        .then((response) => {
          toast.success(`Payment updated successfully`);
          setLoading(false);
          setAddPaymentVisible(false);
          resetForm();
          loadAllPayments();
          if (onSuccess) onSuccess();
        })
        .catch((error) => {
          setLoading(false);
          toast.error(`Error While Saving`);
        });
    } else {
      axios({
        method: "post",
        url: `${process.env.REACT_APP_API}/admin/jobs/payments/add`,
        data: submitFormData,
      })
        .then((response) => {
          toast.success(`Payment added successfully`);
          setLoading(false);
          setAddPaymentVisible(false);
          resetForm();
          loadAllPayments();
          if (onSuccess) onSuccess();
        })
        .catch((error) => {
          setLoading(false);
          toast.error(`Error While Saving`);
        });
    }
  };

  const resetForm = () => {
    setFormData({
      job_id: jobId,
      amount: null,
      due_date: "",
      paid: false,
      type: paymentType,
      method: "",
      id: -1,
    });
  };

  const deletePayment = (meta) => {
    const id = meta.rowData[0];
    Swal.fire({
      title: "Please Confirm Your Action ?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      icon: "warning",
    }).then((result) => {
      if (result.isConfirmed) {
        axios({
          method: "post",
          url: `${process.env.REACT_APP_API}/admin/jobs/payments/delete`,
          data: { id: id },
        })
          .then((response) => {
            toast.success(`Payment deleted successfully`);
            loadAllPayments();
          })
          .catch((error) => {
            toast.error(`Error deleting payment`);
          });
      }
    });
  };

  const updatePayment = (meta) => {
    const id = meta.rowData[0];
    axios({
      method: "get",
      url: `${process.env.REACT_APP_API}/admin/jobs/payments/getById/${id}`,
    })
      .then((response) => {
        setFormData({
          job_id: jobId,
          amount: response.data.amount,
          due_date: response.data.due_date,
          method: response.data.method,
          paid: response.data.paid == 1 ? true : false,
          type: response.data.type,
          id: response.data.id,
        });
        setPaymentType(response.data.type);
        setAddPaymentVisible(true);
      })
      .catch((error) => {
        toast.error(`Error loading payment data`);
      });
  };

  const payments = ["Cash", "Whish", "OMT", "Bank Transfer"];

  const paymentsColumns = [
    {
      name: "id",
      label: "ID",
    },
    {
      name: "amount",
      label: "Amount",
    },
    {
      name: "due_date",
      label: "Date",
    },
    {
      name: "type",
      label: "Type",
      options: {
        customBodyRender: (value) => {
          return value ? (
            <>{value == "in" ? "From Client" : "To Worker"}</>
          ) : (
            <></>
          );
        },
      },
    },
    {
      name: "paid",
      label: "Paid",
      options: {
        customBodyRender: (value) => {
          return (
            <input
              disabled
              className="form-check-input"
              type="checkbox"
              readOnly
              checked={value == 1 ? true : false}
            />
          );
        },
      },
    },
    {
      name: "method",
      label: "Payment Method",
      options: {
        customBodyRender: (value) => (value ? payments[value - 1] : ""),
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (value, meta) => {
          return (
            <div>
              <button
                className="btn btn-info mx-1"
                onClick={() => {
                  updatePayment(meta);
                }}
              >
                Update
              </button>
              <button
                className="btn btn-danger mx-1"
                onClick={() => {
                  deletePayment(meta);
                }}
              >
                Delete
              </button>
            </div>
          );
        },
      },
    },
  ];

  const paymentsOptions = {
    filter: true,
    rowsPerPage: 50,
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: "none",
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress />
        ) : (
          "There is no matching data to display"
        ),
      },
    },
  };

  const addPayment = (type) => {
    setPaymentType(type);
    setFormData({
      job_id: jobId,
      amount: null,
      due_date: "",
      paid: false,
      type: type,
      method: "",
      id: -1,
    });
    setAddPaymentVisible(true);
  };

  return (
    <div>
      <div className="mb-4">
        <button
          className="btn btn-primary me-2"
          onClick={() => addPayment("in")}
        >
          Add Client Payment
        </button>
        <button
          className="btn btn-primary"
          onClick={() => addPayment("out")}
        >
          Add Worker Payment
        </button>
      </div>

      <div className="row">
        <div className="col-md-12">
          <CustomMuiDataTable
            title={"Client Payments"}
            data={clientPayments}
            columns={paymentsColumns}
            options={paymentsOptions}
          />
        </div>
        <div className="col-md-12">
          <CustomMuiDataTable
            title={"Worker Payments"}
            data={workerPayments}
            columns={paymentsColumns}
            options={paymentsOptions}
          />
        </div>
      </div>

      {/* Add/Edit Payment Modal */}
      {addPaymentVisible && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '600px',
            maxWidth: '90%'
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>{formData.id > 0 ? "Update" : "Add"} Payment</h4>
              <button
                type="button"
                className="close-modal-button"
                onClick={() => setAddPaymentVisible(false)}
              >
                &times;
              </button>
            </div>

            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="amount">Amount</label>
                  <input
                    type="text"
                    className="form-control"
                    id="amount"
                    onChange={handleInputChange}
                    value={formData.amount}
                  />
                </div>
              </div>

              <div className="col-sm-6">
                <div className="form-group">
                  <div className="dateView">
                    <label
                      style={{
                        fontSize: "18px",
                        marginTop: "10px",
                        marginRight: "10px",
                      }}
                      htmlFor="due_date"
                    >
                      Due Date
                    </label>
                    <input
                      type="date"
                      style={{
                        width: 200,
                        height: 50,
                        border: "1px solid #D3D3D3",
                        borderRadius: 5,
                      }}
                      id="due_date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="col-sm-6">
                <SingleCustomAutocomplete
                  disabled={paymentType}
                  filedName="type"
                  label="Type"
                  list={[
                    {
                      id: "in",
                      name: "From Client",
                    },
                    {
                      id: "out",
                      name: "To worker",
                    },
                  ]}
                  value={formData.type}
                  listKey="id"
                  description="name"
                  customOnChange={customOnChange}
                />
              </div>

              <div className="col-sm-6">
                <SingleCustomAutocomplete
                  filedName="method"
                  label="Method of Payment"
                  list={[
                    {
                      id: 1,
                      name: "Cash",
                    },
                    {
                      id: 2,
                      name: "Whish",
                    },
                    {
                      id: 3,
                      name: "OMT",
                    },
                    {
                      id: 4,
                      name: "Bank Transfer",
                    },
                  ]}
                  value={formData.method}
                  listKey="id"
                  description="name"
                  customOnChange={customOnChange}
                />
              </div>

              <div className="col-sm-12">
                <div className="form-group" style={{ padding: "20px" }}>
                  <input
                    className="form-control-checkbox"
                    id="paid"
                    type="checkbox"
                    checked={formData.paid}
                    onChange={handleInputChange}
                  />
                  <label style={{ paddingLeft: 10 }} htmlFor="paid">
                    Paid
                  </label>
                </div>
              </div>

              <div className="col-sm-12 mt-3">
                {loading ? (
                  <CircularProgress />
                ) : (
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setAddPaymentVisible(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSavePayment}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsTab; 